import { Button, DialogTrigger, makeStyles, mergeClasses, tokens, Tooltip } from '@fluentui/react-components';
import { KeyboardEventArgs } from '@syncfusion/ej2-base';
import {
    Aggregate,
    AggregateColumnDirective,
    AggregateColumnsDirective,
    AggregateDirective,
    AggregatesDirective,
    ColumnDirective,
    ColumnModel,
    ColumnsDirective,
    Edit,
    EditMode,
    Filter,
    GridComponent,
    Inject,
    NewRowPosition,
    Resize,
    RowDataBoundEventArgs,
    RowSelectingEventArgs,
    SelectionSettingsModel,
    Sort,
    Toolbar,
} from '@syncfusion/ej2-react-grids';
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditableGridStyles } from '../../../hooks/useEditableGridStyles';
import { useFunctionalAccess } from '../../../hooks/useFunctionalAccess';
import { ColumnAlignment, ColumnType, DetailedWorkspaceDto, DetailedWorkspaceDtoRowsItem } from '../../../model';
import { ROW_NO_COLUMN_NAME } from '../../../utils/DetailedWorkspace.constants';
import {
    cancelEditModeOnShiftTab,
    DEFAULT_NUMBER_FORMAT,
    getGridColumnEditType,
    getGridColumnType,
    IFormViewerMethods,
    ImperativeComponent,
    parseNumericValue,
    transformColumnarReportToTableData,
} from '../../../utils/DetailedWorkspaceUtils';
import {
    getUniqueRowNosFromCellSelection,
    getUniqueViewRowIndicesFromCellSelection,
    viewIndicesToRowNumbers,
} from '../../../utils/RowTableCellSelectionUtils';
import {
    attachSyncfusionDeleteToolbarFluentTooltip,
    cancelScheduledToolbarFlushRaf,
    createInitialToolbarFlushRafHandle,
    getSyncfusionToolbarModule,
    scheduleToolbarFlushRaf,
    syncfusionDeleteToolbarItemId,
    type IToolbarFluentTooltipState,
} from '../../../utils/SyncfusionGridToolbarUtils';
import {
    enableSingleClickEditOnCells,
    getInputNameFromClipboardEvent,
    isExcelClipboardContent,
    parsePasteContentToTable,
    TEXT_PLAIN_CLIPBOARD_CONTENT_TYPE,
} from '../../../utils/TableUtils';
import { DefaultTooltipHostV9 } from '../../common/DefaultTooltipHostV9';
import ConfirmationDialogV9 from '../../dialog/ConfirmationDialogV9';
import DefaultGrid from '../../grid/DefaultGrid';

const DISABLED_COLUMN_CELL_CLASS = 'comet-rowdw-column-disabled';

/** Prevents duplicate listeners if `dataBound` runs again on the same toolbar node. */
const ROW_DELETE_TOOLBAR_FLUENT_TOOLTIP_ATTR = 'data-row-delete-fluent-tooltip';

export interface IRowTableFormViewerProps {
    detailedWorkspace: DetailedWorkspaceDto | null | undefined;
    onDataChange?: () => void;
    disabled?: boolean;
    onDeleteAllRows?: () => void;
}

const useStyles = makeStyles({
    byRowStatementStyles: {
        height: '100%',
        '& .e-gridcontent .e-content': {
            overflow: 'auto !important',
        },
        [`& td.${DISABLED_COLUMN_CELL_CLASS}`]: {
            backgroundColor: tokens.colorNeutralBackground4,
            color: `${tokens.colorNeutralForeground4} !important`,
        },
        '& .e-headercelldiv': {
            display: 'flex !important',
        },
    },
    tableContent: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
    },
    totalRow: {
        fontWeight: tokens.fontWeightSemibold,
        fontSize: '16px',
        verticalAlign: 'top',
        lineHeight: '22px',
    },
});

const RowTableFormViewer: ImperativeComponent<IFormViewerMethods, IRowTableFormViewerProps> = React.forwardRef<
    IFormViewerMethods,
    IRowTableFormViewerProps
>(({ detailedWorkspace, onDataChange, onDeleteAllRows, disabled }, gridRef) => {
    const { functionalAccess } = useFunctionalAccess();
    const innerGridRef = useRef<GridComponent>(null);
    const [rowDeleteConfirmOpen, setRowDeleteConfirmOpen] = useState(false);
    const pendingDeleteRowNosRef = useRef<number[]>([]);
    const editToolbarFlushRafRef = useRef(createInitialToolbarFlushRafHandle());
    const pendingDeleteRowNosSnapshotRef = useRef<number[]>([]);
    const [currentTooltipState, setCurrentTooltipState] = useState<IToolbarFluentTooltipState>({ text: null, target: null });
    const deleteToolbarTooltipTextRef = useRef('');

    function getCurrentRows(): Record<string, unknown>[] {
        // Get all the rows from the grid, ignoring filters
        const result: Record<string, unknown>[] = [];
        const currentRows = innerGridRef.current?.getDataModule().dataManager.dataSource.json;

        if (currentRows) {
            result.push(...(currentRows as Record<string, unknown>[]));
        }

        return result;
    }

    // Track if value changed via cellSave
    const editValueChangedRef = useRef<boolean>(false);
    const styles = useStyles();
    const { gridComponentCSSClass, gridContainerStyles, toolbarStyles, footerStyles } = useEditableGridStyles();
    const byRowStatementClass = mergeClasses(
        toolbarStyles,
        gridComponentCSSClass,
        footerStyles,
        gridContainerStyles,
        styles.byRowStatementStyles
    );

    const getCurrentRowCount = () => {
        const currentRows = getCurrentRows();
        return Array.isArray(currentRows) ? currentRows.length : 0;
    };

    const hasUnsavedEditChanges = () => {
        const grid = innerGridRef.current;
        if (!grid) return false;

        // Rows added or deleted (same as add/edit: compare current data to initial)
        const initialRowCount = detailedWorkspace?.rows?.length ?? 0;
        const currentRowCount = getCurrentRowCount();
        if (currentRowCount !== initialRowCount) return true;

        // In edit form: check form value changes
        if (!grid.isEdit) return false;

        if (editValueChangedRef.current) return true;

        const form = grid.element.querySelector('.e-gridform');
        if (form) {
            const inputs = form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input:not([type="hidden"]), textarea');
            for (const input of inputs) {
                if (input.value !== input.defaultValue) {
                    return true;
                }
            }
        }

        return false;
    };
    const cleanUpState = () => {
        editValueChangedRef.current = false;
    };

    useImperativeHandle(gridRef, () => ({
        getUpdatedDetailedWorkspace,
        hasUnsavedEditChanges,
        cleanUpState,
    }));

    const toolbarOptions = ['Add', 'Delete'];

    const editOptions = {
        allowEditing: !disabled && functionalAccess?.amountDataEntryModifyEnabled,
        allowAdding: !disabled && functionalAccess?.amountDataEntryModifyEnabled,
        allowEditOnDblClick: false,
        allowDeleting: !disabled && functionalAccess?.amountDataEntryModifyEnabled,
        newRowPosition: 'Bottom' as NewRowPosition,
        mode: 'Batch' as EditMode,
        showConfirmDialog: false,
    };

    const selectionSettings: SelectionSettingsModel = { mode: 'Cell' };

    const { t } = useTranslation();
    deleteToolbarTooltipTextRef.current = t('deleteRowToolbarTooltip');

    useEffect(() => {
        return () => cancelScheduledToolbarFlushRaf(editToolbarFlushRafRef);
    }, []);

    const getUpdatedDetailedWorkspace = () => {
        innerGridRef.current?.editModule.endEdit();

        const updatedRows = getCurrentRows();
        const updatedDetailedWorkspace: DetailedWorkspaceDto = {
            ...detailedWorkspace,
            rows: updatedRows as any,
        };

        return updatedDetailedWorkspace;
    };

    const { columns, dataSource } = useMemo(() => {
        return transformColumnarReportToTableData(detailedWorkspace);
    }, [detailedWorkspace]);

    // Using useCallback to prevent the function from being recreated on every render
    const customPasteHandler = useCallback(
        (event: ClipboardEvent, rowKey?: number) => {
            if (isExcelClipboardContent(event)) {
                event.preventDefault();
                const pastedText = event.clipboardData?.getData(TEXT_PLAIN_CLIPBOARD_CONTENT_TYPE);

                if (innerGridRef.current) {
                    innerGridRef.current.editModule.endEdit();
                    innerGridRef.current.clearSelection();
                }

                const { newRows, changedRows } = parsePasteContentToTable(
                    pastedText ?? '',
                    columns,
                    rowKey ?? 0,
                    getInputNameFromClipboardEvent(event),
                    detailedWorkspace?.columnSchema,
                    getCurrentRows()
                );

                if (innerGridRef.current && (newRows.length > 0 || changedRows.length > 0)) {
                    innerGridRef.current.batchUpdate({
                        addedRecords: newRows,
                        changedRecords: changedRows,
                    });
                }
            }

            onDataChange?.();
        },
        [columns]
    );

    const editCell = (eventTarget: HTMLElement) => {
        if (disabled || !functionalAccess?.amountDataEntryModifyEnabled) {
            return;
        }

        const targetCell = eventTarget.closest('.e-rowcell') as HTMLElement | null;
        if (!targetCell) return;

        const grid = innerGridRef.current;
        if (!grid?.editModule) return;

        const rowIndex = parseInt(targetCell.parentElement?.getAttribute('aria-rowindex') as string);
        const columnIndex = parseInt(targetCell.getAttribute('aria-colindex') as string) - 1;
        const column = grid.getColumnByIndex(columnIndex);

        if (column?.field === ROW_NO_COLUMN_NAME) {
            return;
        }

        const columnDefinition = detailedWorkspace?.columnSchema?.[column?.field ?? ''];
        const rowDefinition: DetailedWorkspaceDtoRowsItem | undefined = getCurrentRows()[rowIndex - 1] as DetailedWorkspaceDtoRowsItem;

        // Match ColumnTableFormViewer: allow edit when columnSchema is absent; only block when schema marks column disabled.
        if (column?.field && !columnDefinition?.disabled) {
            grid.editModule.editCell(rowIndex - 1, column.field);

            targetCell.querySelectorAll('input').forEach((input) => {
                input.addEventListener('paste', (event: ClipboardEvent) =>
                    customPasteHandler(event, (rowDefinition?.RowNo ?? 0) as number)
                );
            });

            scheduleEditToolbarItemStateUpdate();
        }
    };

    if (!detailedWorkspace || !columns.length) {
        return <div className={styles.tableContent}>{t('noDataAvailable')}</div>;
    }

    const columnSummaryTemplate = () => {
        return <span className={styles.totalRow}>{t('total')}</span>;
    };

    const renderAggregateColumns = () => {
        return columns.map((col) => {
            if (!col.summary) {
                return null;
            }

            return (
                <AggregateColumnDirective
                    key={col.field}
                    type={col.summary}
                    field={col.field || ''}
                    format={col.summary ? DEFAULT_NUMBER_FORMAT : ''}
                />
            );
        });
    };
    const handleRowDataBound = (args: RowDataBoundEventArgs) => {
        const rowData = args.data as Record<string, unknown> | undefined;
        if (rowData && !rowData.RowNo) {
            const currentRows = getCurrentRows();
            const allRows = [...currentRows];
            const maxRowNo = allRows.length ? Math.max(...allRows.map((r: any) => Number(r.RowNo ?? 0))) : 0;
            rowData.RowNo = maxRowNo + 1;
        }
    };

    const rowSelecting = (args: RowSelectingEventArgs) => {
        const eventTarget = args.target as HTMLElement | undefined;
        if (!eventTarget?.closest?.('.e-toolbar')) {
            return;
        }
        const toolbarButton = eventTarget.closest?.('.e-btn') as HTMLElement | undefined;
        const toolbarButtonId = toolbarButton?.id ?? '';
        if (toolbarButtonId.endsWith('_add')) {
            innerGridRef.current?.clearSelection();
        }
    };

    const performDeleteRowsByRowNo = (rowNos: number[]) => {
        const grid = innerGridRef.current;
        if (!grid || rowNos.length === 0) {
            return;
        }
        grid.editModule?.endEdit();
        grid.editModule?.batchSave();

        const toRemove = new Set(rowNos.map((n) => Number(n)));
        const currentData = grid.getDataModule().dataManager.dataSource.json as Record<string, unknown>[];
        const next = currentData.filter((row) => !toRemove.has(Number(row.RowNo)));
        grid.dataSource = [...next];
        grid.clearSelection();
        grid.refresh();
        onDataChange?.();
        scheduleEditToolbarItemStateUpdate();
    };

    const requestDeleteSelectedRows = () => {
        const grid = innerGridRef.current;
        if (!grid) {
            return;
        }
        let rowNos = getUniqueRowNosFromCellSelection(grid);
        if (rowNos.length === 0 && pendingDeleteRowNosSnapshotRef.current.length > 0) {
            rowNos = [...pendingDeleteRowNosSnapshotRef.current];
        }
        pendingDeleteRowNosSnapshotRef.current = [];
        if (rowNos.length === 0) {
            return;
        }
        const currentRowCount = getCurrentRowCount();
        if (rowNos.length === currentRowCount) {
            onDeleteAllRows?.();
            return;
        }
        pendingDeleteRowNosRef.current = rowNos;
        setRowDeleteConfirmOpen(true);
    };

    const onConfirmDeleteRows = () => {
        const rowNos = pendingDeleteRowNosRef.current;
        pendingDeleteRowNosRef.current = [];
        setRowDeleteConfirmOpen(false);
        if (rowNos.length > 0) {
            performDeleteRowsByRowNo(rowNos);
        }
    };

    const onCancelDeleteRows = () => {
        pendingDeleteRowNosRef.current = [];
        setRowDeleteConfirmOpen(false);
    };

    const toolbarClick = (args: { cancel?: boolean; item?: { properties?: { text?: string } } }) => {
        if (args.item?.properties?.text?.includes('Delete')) {
            args.cancel = true;
            requestDeleteSelectedRows();
        }
    };

    const getFirstEditableDataField = (grid: GridComponent): string | undefined => {
        for (const col of grid.getVisibleColumns()) {
            const field = col.field;
            if (!field || field === ROW_NO_COLUMN_NAME) continue;
            if (col.type === 'checkbox') continue;
            if (col.allowEditing === false) continue;
            if (detailedWorkspace?.columnSchema?.[field]?.disabled) continue;
            return field;
        }
        return undefined;
    };

    const onBatchAdd = (args: { row?: Element | Element[] }) => {
        onDataChange?.();
        innerGridRef.current?.editModule.batchSave();
        if (disabled) return;

        const grid = innerGridRef.current;
        if (!grid) return;

        const field = getFirstEditableDataField(grid);
        if (!field) return;

        const tr = Array.isArray(args.row) ? args.row[0] : args.row;
        const dataRows = grid.getDataRows();
        let rowIndex = tr ? dataRows.indexOf(tr as HTMLTableRowElement) : -1;
        if (rowIndex < 0) {
            rowIndex = grid.editSettings.newRowPosition === 'Top' ? 0 : Math.max(0, dataRows.length - 1);
        }

        // Timeout needed due to the batch save triggering before the new row is created
        setTimeout(() => {
            editCell(grid.getCellFromIndex(rowIndex, grid.getColumnIndexByField(field)) as HTMLElement);
        }, 100);
    };

    // Track changes as soon as user modifies a cell value (before row save)
    const onCellSave = (args: any) => {
        if (args.previousValue !== args.value) {
            editValueChangedRef.current = true;
            onDataChange?.();
        }
        scheduleEditToolbarItemStateUpdate();
    };

    // Fixes accessibility navigation issues with the table
    const onDataBound = () => {
        const gridComponent = innerGridRef.current;
        if (gridComponent) {
            const gridHeader = gridComponent.element.querySelector<HTMLElement>('.e-gridheader');
            if (gridHeader) {
                gridHeader.tabIndex = 0;
            }

            scheduleEditToolbarItemStateUpdate();
            attachSyncfusionDeleteToolbarFluentTooltip({
                grid: gridComponent,
                attachedMarkerAttr: ROW_DELETE_TOOLBAR_FLUENT_TOOLTIP_ATTR,
                getTooltipText: () => deleteToolbarTooltipTextRef.current,
                setTooltipState: setCurrentTooltipState,
                onPointerDownCapture: () => {
                    const grid = innerGridRef.current;
                    if (!grid) {
                        return;
                    }
                    pendingDeleteRowNosSnapshotRef.current = getUniqueRowNosFromCellSelection(grid);
                },
            });
        }
    };

    // Edit: single-row cell selection. Delete: only while batch-editing a cell (avoids flicker from transient selection).
    const flushEditToolbarItemState = () => {
        const grid = innerGridRef.current;
        const toolbarModule = getSyncfusionToolbarModule(grid);
        const gridId = grid?.element?.id;
        if (!gridId || !toolbarModule) {
            return;
        }

        if (disabled) {
            toolbarModule.enableItems([`${gridId}_edit`], false);
            toolbarModule.enableItems([syncfusionDeleteToolbarItemId(gridId)], false);
            return;
        }

        const viewRowIndices = getUniqueViewRowIndicesFromCellSelection(grid);
        const rowNumbersForDelete = viewIndicesToRowNumbers(grid, viewRowIndices);
        const deleteEnabled =
            grid.isEdit === true && rowNumbersForDelete.length > 0 && functionalAccess?.amountDataEntryModifyEnabled !== false;

        toolbarModule.enableItems([`${gridId}_edit`], viewRowIndices.length === 1);
        toolbarModule.enableItems([syncfusionDeleteToolbarItemId(gridId)], deleteEnabled);
    };

    const scheduleEditToolbarItemStateUpdate = () => {
        scheduleToolbarFlushRaf(editToolbarFlushRafRef, flushEditToolbarItemState);
    };

    const onKeyDown = (e: KeyboardEventArgs) => {
        cancelEditModeOnShiftTab(e, innerGridRef);

        const isDeleteKey = e.key === 'Delete' || e.code === 'Delete';
        if (!isDeleteKey) {
            return;
        }
        if (rowDeleteConfirmOpen || disabled || functionalAccess?.amountDataEntryModifyEnabled === false) {
            return;
        }
        const grid = innerGridRef.current;
        if (!grid) {
            return;
        }
        const viewRowIndices = getUniqueViewRowIndicesFromCellSelection(grid);
        const rowNosForDelete = viewIndicesToRowNumbers(grid, viewRowIndices);
        const deleteEnabled = grid.isEdit === true && rowNosForDelete.length > 0;
        if (!deleteEnabled) {
            return;
        }
        requestDeleteSelectedRows();
        (e as KeyboardEventArgs & { cancel?: boolean }).cancel = true;
        e.preventDefault();
    };

    const editConfig = {
        write: (args: any) => {
            if (detailedWorkspace?.columnSchema && args.column.field) {
                const columnSchema = detailedWorkspace?.columnSchema?.[args.column.field];
                const maxLength = columnSchema?.maxLength;
                const type = columnSchema?.type;
                args.element.disabled = columnSchema?.disabled;

                if (maxLength) {
                    args.element.maxLength = maxLength;
                }

                if (type === 'int') {
                    const input = args.element as HTMLInputElement;
                    input.value = String(parseNumericValue(input.value));
                    input.oninput = () => {
                        input.value = String(parseNumericValue(input.value));
                    };
                }
            }

            return true;
        },
    };

    const columnHeaderTemplate = (item: ColumnModel) => {
        const headerText = item.headerText ?? '';
        return (
            <DefaultTooltipHostV9 appearance='inverted' withArrow content={headerText}>
                {headerText}
            </DefaultTooltipHostV9>
        );
    };

    const handleGridCreated = () => {
        enableSingleClickEditOnCells(innerGridRef, editCell);
        queueMicrotask(() => {
            const grid = innerGridRef.current;
            const toolbarModule = getSyncfusionToolbarModule(grid);
            const gridId = grid?.element?.id;
            if (grid && toolbarModule && gridId) {
                toolbarModule.enableItems([`${gridId}_edit`], false);
                toolbarModule.enableItems([syncfusionDeleteToolbarItemId(gridId)], false);
            }
        });
    };

    return (
        <>
            <ConfirmationDialogV9
                open={rowDeleteConfirmOpen}
                title={t('deleteRowConfirmationTitle')}
                content={t('deleteRowConfirmationMessage')}
                buttons={
                    <>
                        <DialogTrigger>
                            <Button
                                onClick={onCancelDeleteRows}
                                data-analytics-button-name='confirmation_dialog_cancel'
                                aria-label={t('cancel').toString()}
                            >
                                {t('cancel')}
                            </Button>
                        </DialogTrigger>
                        <Button
                            appearance='primary'
                            onClick={onConfirmDeleteRows}
                            data-analytics-button-name='confirmation_dialog_save'
                            aria-label={t('delete').toString()}
                        >
                            {t('delete')}
                        </Button>
                    </>
                }
            />
            <div className={styles.tableContent}>
                <div className={byRowStatementClass}>
                    <Tooltip
                        relationship='description'
                        visible={!!currentTooltipState.target}
                        content={currentTooltipState.text ?? ''}
                        appearance='inverted'
                        withArrow
                        positioning={{ target: currentTooltipState.target, position: 'after' }}
                    />
                    <DefaultGrid
                        ref={innerGridRef}
                        dataSource={dataSource}
                        filterSettings={{ type: 'CheckBox' }}
                        toolbar={toolbarOptions}
                        toolbarClick={toolbarClick}
                        allowResizing
                        allowFiltering
                        allowSorting
                        rowSelecting={rowSelecting}
                        cellSelected={scheduleEditToolbarItemStateUpdate}
                        cellDeselected={scheduleEditToolbarItemStateUpdate}
                        selectionSettings={selectionSettings}
                        batchAdd={onBatchAdd}
                        cellSave={onCellSave}
                        rowDataBound={handleRowDataBound}
                        dataBound={onDataBound}
                        height='100%'
                        editSettings={editOptions}
                        created={handleGridCreated}
                        keyPressed={onKeyDown}
                    >
                        <ColumnsDirective>
                            <ColumnDirective field={ROW_NO_COLUMN_NAME} width='50' isPrimaryKey visible={false} />

                            {columns.map((col) => {
                                const field = col.field ?? '';
                                const isSchemaDisabled = Boolean(field && detailedWorkspace?.columnSchema?.[field]?.disabled);
                                return (
                                    <ColumnDirective
                                        key={col.field}
                                        visible={col.field !== ROW_NO_COLUMN_NAME}
                                        editType={getGridColumnEditType(col.type)}
                                        field={field}
                                        headerText={col.name || ''}
                                        headerTemplate={columnHeaderTemplate}
                                        type={getGridColumnType(col.type)}
                                        edit={editConfig}
                                        width={150}
                                        textAlign={col.columnAlignment === ColumnAlignment.Right ? 'Right' : 'Left'}
                                        format={col.type === ColumnType.Numeric ? DEFAULT_NUMBER_FORMAT : ''}
                                        customAttributes={isSchemaDisabled || disabled ? { class: DISABLED_COLUMN_CELL_CLASS } : undefined}
                                    />
                                );
                            })}
                        </ColumnsDirective>
                        <AggregatesDirective>
                            <AggregateDirective>
                                <AggregateColumnsDirective>
                                    <AggregateColumnDirective
                                        type='Custom'
                                        customAggregate={() => ''}
                                        footerTemplate={columnSummaryTemplate}
                                    />
                                    {renderAggregateColumns()}
                                </AggregateColumnsDirective>
                            </AggregateDirective>
                        </AggregatesDirective>
                        <Inject services={[Sort, Resize, Filter, Aggregate, Edit, Toolbar]} />
                    </DefaultGrid>
                </div>
            </div>
        </>
    );
});

export default RowTableFormViewer;
